import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, Coffee, LogOut, LogIn, Cigarette, X } from 'lucide-react';

interface EventType {
  id: string;
  name: string;
  code: string;
  description?: string;
  color: string;
  icon?: string;
  order: number;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

interface EventTypeSelectorProps {
  employee: Employee;
  eventTypes: EventType[];
  onSelectEventType: (eventTypeId: string) => void;
  isLoading?: boolean;
  onCancel: () => void;
  availableActions?: string[];
  currentState?: string;
  autoBreak?: boolean;
}

const getEventIcon = (code: string) => {
  switch (code) {
    case 'WORK_START':
      return <LogIn className="w-6 h-6" />;
    case 'WORK_END':
      return <LogOut className="w-6 h-6" />;
    case 'BREAK_START':
    case 'BREAK_END':
      return <Coffee className="w-6 h-6" />;
    case 'SMOKE_START':
    case 'SMOKE_END':
      return <Cigarette className="w-6 h-6" />;
    default:
      return <Clock className="w-6 h-6" />;
  }
};

const getStateMessage = (currentState: string) => {
  switch (currentState) {
    case 'AT_WORK':
      return { message: 'Jesteś w pracy', color: 'text-green-600' };
    case 'ON_BREAK':
      return { message: 'Jesteś na przerwie', color: 'text-orange-600' };
    case 'SMOKING':
      return { message: 'Jesteś na papierosie', color: 'text-gray-600' };
    case 'OUTSIDE_WORK':
      return { message: 'Nie jesteś w pracy', color: 'text-red-600' };
    default:
      return { message: 'Status nieznany', color: 'text-gray-600' };
  }
};

export const EventTypeSelector: React.FC<EventTypeSelectorProps> = ({
  employee,
  eventTypes,
  onSelectEventType,
  isLoading,
  onCancel,
  availableActions = [],
  currentState = 'OUTSIDE_WORK',
  autoBreak = false,
}) => {
  const sortedEventTypes = [...eventTypes].sort((a, b) => a.order - b.order);
  const filteredEventTypes = autoBreak
    ? sortedEventTypes.filter(et => et.code !== 'BREAK_START' && et.code !== 'BREAK_END')
    : sortedEventTypes;
  const stateInfo = getStateMessage(currentState);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold text-green-600">
            Witaj, {employee.firstName} {employee.lastName}!
          </CardTitle>
          <div className="mt-2">
            <Badge variant="outline" className={`${stateInfo.color} border-current`}>
              {stateInfo.message}
            </Badge>
          </div>
          <CardDescription className="text-lg mt-2">
            Wybierz typ zdarzenia:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Event Type Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredEventTypes.map((eventType) => {
              const isAvailable = availableActions.includes(eventType.code);
              return (
                <Button
                  key={eventType.id}
                  variant="outline"
                  size="xl"
                  className={`h-20 text-lg font-semibold flex flex-col items-center justify-center space-y-1 transition-transform ${
                    isAvailable 
                      ? 'hover:scale-105' 
                      : 'opacity-50 cursor-not-allowed bg-gray-100'
                  }`}
                  style={{
                    borderColor: isAvailable ? eventType.color : '#d1d5db',
                    backgroundColor: isAvailable ? `${eventType.color}15` : '#f9fafb',
                  }}
                  onClick={() => isAvailable && onSelectEventType(eventType.id)}
                  disabled={isLoading || !isAvailable}
                >
                  <div className="flex items-center space-x-2">
                    {getEventIcon(eventType.code)}
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: isAvailable ? eventType.color : '#d1d5db' }}
                    />
                  </div>
                  <span>{eventType.name}</span>
                  {eventType.description && (
                    <span className="text-sm text-gray-500">{eventType.description}</span>
                  )}
                  {!isAvailable && (
                    <span className="text-xs text-red-500 text-center">
                      Niedostępne
                    </span>
                  )}
                </Button>
              );
            })}
          </div>

          {autoBreak && (
            <div className="text-center text-sm text-muted-foreground bg-orange-50 p-3 rounded-lg border border-orange-200">
              <Coffee className="w-4 h-4 inline-block mr-1 text-orange-500" />
              Przerwy śniadaniowe naliczane automatycznie
            </div>
          )}

          {/* Cancel Button */}
          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              size="lg"
              className="text-lg px-8 flex items-center space-x-2"
              onClick={onCancel}
              disabled={isLoading}
            >
              <X className="w-4 h-4" />
              <span>Anuluj</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};